import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { GroundingBadge, GroundingReport } from "../components/GroundingReport";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/ui-helpers";
import { Button } from "../components/ui/button";

import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Sparkle, FloppyDisk, FileXls, UploadSimple, Trash, ShieldCheck, FileText, Folders, CircleNotch, BookOpen, Plus, Warning, ArrowsLeftRight, ClipboardText, CheckCircle,
} from "@phosphor-icons/react";
import { DataResidencyNote } from "../components/DataResidencyNote";

const DISPOS = ["No Change", "Minor", "Significant", "New", "Retired"];
const KIND_META = {
  requirement: { label: "Requirement", cls: "bg-[#071B3E] text-white border-[#071B3E]", indent: "" },
  subpart: { label: "Part / Section", cls: "bg-indigo-100 text-indigo-800 border-indigo-300", indent: "md:ml-8 border-l-4 border-l-indigo-300" },
  attachment: { label: "Attachment", cls: "bg-violet-100 text-violet-800 border-violet-300", indent: "border-l-4 border-l-violet-400" },
  section: { label: "Standard Section", cls: "bg-amber-100 text-amber-800 border-amber-300", indent: "border-l-4 border-l-amber-400" },
  measure: { label: "Measure", cls: "bg-teal-100 text-teal-800 border-teal-300", indent: "md:ml-8 border-l-4 border-l-teal-400" },
};
const DISPO_CLS = {
  "No Change": "bg-muted text-foreground border-border",
  "Minor": "bg-blue-100 text-blue-800 border-blue-300",
  "Significant": "bg-amber-100 text-amber-900 border-amber-300",
  "New": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Retired": "bg-red-100 text-red-800 border-red-300",
};
const SOURCE_KINDS = [
  ["current_std", "Current Standard"], ["future_std", "Future Standard"],
  ["impl_plan", "Implementation Plan"], ["rsaw", "RSAW Template"], ["guidance", "Other Guidance"],
];
const VERDICT_META = {
  high: { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "High confidence" },
  moderate: { cls: "bg-amber-100 text-amber-900 border-amber-300", label: "Moderate confidence" },
  low: { cls: "bg-red-100 text-red-800 border-red-300", label: "Low confidence" },
};
const SUPPORT_META = {
  supported: { cls: "text-emerald-700", label: "Supported" },
  partial: { cls: "text-amber-700", label: "Partial / general practice" },
  unsupported: { cls: "text-red-700", label: "Unsupported" },
};

export default function TransitionWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { canEdit, isSuperadmin } = useAuth();
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState("");
  const [tenantLocked, setTenantLocked] = useState(false);
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [lowConfOnly, setLowConfOnly] = useState(false);
  const [lowConfThreshold, setLowConfThreshold] = useState(75);
  const editable = canEdit && !tenantLocked;

  const load = useCallback(() => {
    api.get(`/transitions/${id}`).then((r) => setT(r.data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); nav("/gaps"); });
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/tenant/me", { __silent402: true }).then(({ data }) => setTenantLocked(!!data?.locked)).catch(() => {});
  }, []);

  const setRow = (rid, patch) =>
    setT((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.id === rid ? { ...r, ...patch } : r)) }));

  const verifyRow = async (rid) => {
    setBusy(`verify-${rid}`);
    try {
      const { data } = await api.post(`/transitions/${id}/rows/${rid}/verify-grounding`);
      setRow(rid, { grounding_check: data });
      toast.success(`Grounding checked — ${data.confidence}% confidence`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grounding check failed");
    } finally { setBusy(""); }
  };
  const improveRow = async (rid) => {
    setBusy(`improve-${rid}`);
    try {
      const { data } = await api.post(`/transitions/${id}/rows/${rid}/improve-narrative`);
      setRow(rid, { good_looks_like: data.good_looks_like, grounding_check: null });
      toast.success("Narrative redrafted from the grounding findings — re-verify to score it");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not improve the narrative");
    } finally { setBusy(""); }
  };
  const verifyCrosswalk = async () => {
    setBusy("verify-crosswalk");
    try {
      const { data } = await api.post(`/transitions/${id}/verify-crosswalk-grounding`);
      setT((prev) => ({ ...prev, crosswalk_grounding: data }));
      toast.success(`Crosswalk grounding — ${data.confidence}% confidence`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Crosswalk grounding failed");
    } finally { setBusy(""); }
  };
  const improveLow = async () => {
    setBusy("improve-low");
    try {
      const { data } = await api.post(`/transitions/${id}/improve-low-confidence?threshold=${lowConfThreshold}`);
      toast.success(`Improving ${data.queued} low-confidence row${data.queued === 1 ? "" : "s"} — refresh in ~30s`);
      setTimeout(load, 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nothing to improve");
    } finally { setBusy(""); }
  };
  const verifyAll = async () => {
    setBusy("verify-all");
    try {
      const { data } = await api.post(`/transitions/${id}/verify-grounding`);
      toast.success(`Queued ${data.queued} grounding check${data.queued === 1 ? "" : "s"} — refresh in ~30s`);
      setTimeout(load, 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not queue grounding checks");
    } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.patch(`/transitions/${id}`, {
        name: t.name,
        rows: t.rows.map((r) => ({ id: r.id, smes: r.smes, current_policies: r.current_policies, controls_text: r.controls_text, action_items: r.action_items, disposition: r.disposition })),
      });
      setT(data); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const analyze = async () => {
    setBusy("analyze");
    try {
      const { data } = await api.post(`/transitions/${id}/analyze`);
      const jobId = data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${jobId}`);
          if (j.status === "done") { clearInterval(poll); setBusy(""); load(); toast.success("AI analysis complete"); }
          else if (j.status === "error") { clearInterval(poll); setBusy(""); toast.error("AI analysis failed: " + (j.error || "")); }
        } catch { clearInterval(poll); setBusy(""); }
      }, 4000);
    } catch (e) { setBusy(""); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy("upload");
    try {
      for (const file of list) {
        const fd = new FormData(); fd.append("file", file); fd.append("kind", "auto");
        await api.post(`/transitions/${id}/sources`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Uploaded ${list.length} file${list.length === 1 ? "" : "s"}`); load();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const upload = (e) => uploadFiles(e.target.files);
  const retagSource = async (sid, kind) => {
    try { await api.patch(`/transitions/${id}/sources/${sid}`, { kind }); load(); }
    catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };

  const delSource = async (sid) => { await api.delete(`/transitions/${id}/sources/${sid}`); load(); };

  const applyTerms = async (definition_ids) => {
    setBusy("terms");
    try {
      const { data } = await api.post(`/transitions/${id}/apply-terms`, definition_ids ? { definition_ids } : {});
      toast.success(`Linked ${data.applied} term(s) to ${data.standard_id}`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  const exportStar = async () => {
    try {
      await downloadBlob(`/transitions/${id}/star.xlsx`, `star-${id}.xlsx`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!t) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="transition-workspace">
      <PageHeader title="Standard Transition Workspace" subtitle={`${t.current_standard_id}  →  ${t.future_standard_id}`} />
      <div className="p-8 space-y-6">
        <DataResidencyNote context="STaR is designed to analyze public standards." />
        {tenantLocked && (
          <div data-testid="star-readonly-banner" className="flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-900 px-4 py-2.5 text-sm">
            <span className="flex-1">Your Free Trial has ended — this workspace is <b>read-only</b>. Saving, AI analysis and exports are disabled. Subscribe to ClaroShield Light to resume.</span>
            <Button size="sm" className="rounded-none bg-amber-600 hover:bg-amber-700 shrink-0" onClick={() => nav("/billing")} data-testid="star-readonly-upgrade">Upgrade</Button>
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="sm" className="rounded-none" onClick={() => nav("/gaps")} data-testid="back-readiness"><ArrowLeft size={16} className="mr-1" />NERC Readiness</Button>
          <Input value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} className="rounded-none max-w-md" data-testid="transition-name" disabled={!editable} />
          <span className="text-xs text-muted-foreground">Status: <b>{t.status}</b></span>
          <div className="ml-auto flex items-center gap-2">
            {editable && (
              <Button onClick={analyze} disabled={busy === "analyze"} className="rounded-none" data-testid="analyze-btn">
                {busy === "analyze" ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <Sparkle size={16} className="mr-1.5" weight="fill" />}
                {busy === "analyze" ? "Analyzing…" : "Run AI analysis"}
              </Button>
            )}
            {editable && t.is_new_standard && (
              <Button
                onClick={async () => {
                  setBusy("narratives");
                  try {
                    const r = await api.post(`/transitions/${id}/generate-narratives`, null, { params: { force: false } });
                    toast.success(`AI queued ${r.data.queued} narrative${r.data.queued === 1 ? "" : "s"} — refresh in ~30s`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not queue narrative generation");
                  } finally { setBusy(""); }
                }}
                disabled={busy === "narratives"} variant="outline" className="rounded-none"
                data-testid="star-generate-narratives-btn">
                <Sparkle size={16} className="mr-1.5" />{busy === "narratives" ? "Queuing…" : "Draft narratives"}
              </Button>
            )}
            {editable && t.rows?.some((r) => (r.good_looks_like || "").trim()) && (
              <Button onClick={verifyAll} disabled={busy === "verify-all"} variant="outline" className="rounded-none"
                data-testid="star-verify-all-btn"
                title="Reverse-check every AI narrative against the source material (1 credit per row)">
                {busy === "verify-all" ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <ShieldCheck size={16} className="mr-1.5" />}
                {busy === "verify-all" ? "Queuing…" : "Verify grounding"}
              </Button>
            )}
            {t.rows?.some((r) => r.grounding_check) && (
              <label className="flex items-center gap-1.5 text-xs border border-border px-2 py-1.5 bg-card" data-testid="star-lowconf-filter">
                <input type="checkbox" checked={lowConfOnly} onChange={(e) => setLowConfOnly(e.target.checked)} data-testid="star-lowconf-toggle" />
                Low-confidence only&nbsp;&lt;
                <input type="number" min="0" max="100" value={lowConfThreshold}
                  onChange={(e) => setLowConfThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="w-12 border border-border px-1 py-0.5 text-center" data-testid="star-lowconf-threshold" />%
              </label>
            )}
            {editable && t.rows?.some((r) => r.grounding_check && r.grounding_check.confidence < lowConfThreshold) && (
              <Button onClick={improveLow} disabled={busy === "improve-low"} variant="outline"
                className="rounded-none border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                data-testid="star-improve-low-btn"
                title={`Redraft every verified row below ${lowConfThreshold}% using its grounding findings (1 credit per row)`}>
                {busy === "improve-low" ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <Sparkle size={16} weight="fill" className="mr-1.5" />}
                {busy === "improve-low" ? "Queuing…" : `Improve all < ${lowConfThreshold}%`}
              </Button>
            )}
            {editable && <Button onClick={save} disabled={busy === "save"} variant="outline" className="rounded-none" data-testid="save-btn"><FloppyDisk size={16} className="mr-1.5" />Save</Button>}
            <Button onClick={exportStar} disabled={tenantLocked} title={tenantLocked ? "Exports are disabled — your trial has ended" : undefined} variant="outline" className="rounded-none" data-testid="export-star-btn"><FileXls size={16} className="mr-1.5" />Export STaR</Button>
          </div>
        </div>

        {/* Enforcement / retirement dates from the Standards library */}
        {(t.future_effective_date || t.current_retirement_date) && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="star-key-dates">
            {t.future_effective_date && (
              <span className="inline-flex items-center gap-1.5 border border-green-300 bg-green-50 text-green-800 px-3 py-1 text-xs font-medium rounded-none"
                    data-testid="future-enforcement-date">
                Future standard enforceable: <b className="font-mono">{t.future_effective_date}</b>
              </span>
            )}
            {t.current_retirement_date && (
              <span className="inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-800 px-3 py-1 text-xs font-medium rounded-none"
                    data-testid="current-retirement-date">
                Current standard retires: <b className="font-mono">{t.current_retirement_date}</b>
              </span>
            )}
          </div>
        )}

        {/* Source documents */}
        {canEdit && (
          <div className="border border-border p-4" data-testid="sources-panel">
            <div className="text-sm font-semibold mb-1">Source documents</div>
            <p className="text-xs text-muted-foreground mb-2">Drop the current & future standards, implementation plan, RSAW template, and any other guidance — PDF, Word, or Excel. We auto-detect each file's type; re-tag below if needed.</p>
            <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.xlsx" onChange={upload} className="hidden" data-testid="source-file-input" />
            <div
              data-testid="source-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
              className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed px-4 py-6 cursor-pointer transition-colors ${dragOver ? "border-[#071B3E] bg-blue-50" : "border-border hover:border-slate-400 bg-muted/50"}`}
            >
              <UploadSimple size={22} className="text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">{busy === "upload" ? "Uploading…" : "Drop files here or click to browse"}</span>
              <span className="text-xs text-muted-foreground">Multiple files · PDF, Word (.docx), Excel (.xlsx)</span>
            </div>
            {(t.sources || []).length > 0 && (
              <div className="mt-3 space-y-1">
                {t.sources.map((s) => (
                  <div key={s.id} className="flex items-center gap-2 text-sm border border-border px-3 py-1.5" data-testid={`source-${s.id}`}>
                    <FileText size={14} className="shrink-0" />
                    <span className="text-muted-foreground truncate flex-1 min-w-0">{s.filename}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{s.chars || 0} chars</span>
                    <Select value={s.kind} onValueChange={(v) => retagSource(s.id, v)}>
                      <SelectTrigger className="rounded-none h-7 w-40 text-xs shrink-0" data-testid={`source-kind-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCE_KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="shrink-0" onClick={() => delSource(s.id)}><Trash size={14} /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Requirement crosswalk (current → future, incl. parts/sub-parts) */}
        {(t.crosswalk || []).length > 0 && (
          <div className="border border-amber-200 bg-amber-50/40" data-testid="crosswalk-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200 bg-amber-100/40">
              <ArrowsLeftRight size={16} weight="fill" className="text-amber-700" />
              <span className="font-semibold text-[#071B3E]">Requirement crosswalk — {t.current_standard_id} → {t.future_standard_id}</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">— parts & sub-parts matched by meaning, not number</span>
              <GroundingBadge check={t.crosswalk_grounding} testid="crosswalk-grounding-badge" />
              {editable && (
                <button onClick={verifyCrosswalk} disabled={busy === "verify-crosswalk"}
                  className="ml-auto text-[11px] inline-flex items-center gap-1 border border-amber-300 bg-white px-2 py-0.5 hover:bg-amber-50 disabled:opacity-50"
                  data-testid="verify-crosswalk-btn"
                  title="Reverse-check the CHANGED/MOVED/ADDED calls & notes against the uploaded standards (1 AI credit)">
                  {busy === "verify-crosswalk" ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                  {t.crosswalk_grounding ? "Re-check grounding" : "Verify grounding"}
                </button>
              )}
            </div>
            {t.crosswalk_grounding && (
              <div className="px-4 py-3 border-b border-amber-200" data-testid="crosswalk-grounding-wrap">
                <GroundingReport check={t.crosswalk_grounding} history={t.crosswalk_grounding_history} testid="crosswalk-grounding-report" />
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="crosswalk-table">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-amber-200">
                    <th className="py-2 px-3">{t.current_standard_id} (current)</th>
                    <th className="py-2 px-3">{t.future_standard_id} (future)</th>
                    <th className="py-2 px-3 w-28">Change</th>
                    <th className="py-2 px-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {t.crosswalk.map((c, i) => {
                    const st = (c.status || "").toUpperCase();
                    const cls = { MOVED: "bg-blue-100 text-blue-800 border-blue-300", ADDED: "bg-green-100 text-green-800 border-green-300", REMOVED: "bg-red-100 text-red-800 border-red-300", CHANGED: "bg-amber-100 text-amber-800 border-amber-300", UNCHANGED: "bg-muted text-muted-foreground border-border" }[st] || "bg-muted text-muted-foreground border-border";
                    return (
                      <tr key={i} className="border-b border-amber-100 align-top" data-testid={`crosswalk-row-${i}`}>
                        <td className="py-2 px-3"><span className="font-semibold">{c.current_ref || "—"}</span>{c.current_text ? <span className="block text-xs text-muted-foreground mt-0.5">{c.current_text}</span> : null}{c.current_measure ? <span className="block text-[11px] text-muted-foreground mt-0.5"><b>Measure:</b> {c.current_measure}</span> : null}</td>
                        <td className="py-2 px-3"><span className="font-semibold">{c.future_ref || "—"}</span>{c.future_text ? <span className="block text-xs text-muted-foreground mt-0.5">{c.future_text}</span> : null}{c.future_measure ? <span className="block text-[11px] text-muted-foreground mt-0.5"><b>Measure:</b> {c.future_measure}</span> : null}</td>
                        <td className="py-2 px-3"><span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 font-bold ${cls}`}>{st || "—"}</span></td>
                        <td className="py-2 px-3 text-xs text-muted-foreground">{c.note}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* RSAW audit coverage (Phase 5) */}
        {(t.rsaw_coverage || []).length > 0 && (() => {
          const cov = t.rsaw_coverage;
          const gaps = cov.filter((c) => c.status === "gap").length;
          const prog = cov.filter((c) => c.status === "in_progress").length;
          const covered = cov.filter((c) => c.status === "covered").length;
          const meta = {
            covered: { cls: "bg-green-100 text-green-800 border-green-300", label: "Covered" },
            in_progress: { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "In progress" },
            gap: { cls: "bg-red-100 text-red-800 border-red-300", label: "Gap" },
          };
          return (
            <div className="border border-rose-200 bg-rose-50/40" data-testid="rsaw-coverage-panel">
              <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-rose-200 bg-rose-100/40">
                <ClipboardText size={16} weight="fill" className="text-rose-700" />
                <span className="font-semibold text-[#071B3E]">RSAW audit coverage — {t.future_standard_id}</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">— what the auditor examines vs. what your review covers</span>
                <div className="ml-auto flex items-center gap-1.5 text-[11px] font-semibold" data-testid="rsaw-coverage-summary">
                  <span className="border border-red-300 bg-red-100 text-red-800 px-1.5 py-0.5">{gaps} gap{gaps === 1 ? "" : "s"}</span>
                  <span className="border border-amber-300 bg-amber-100 text-amber-800 px-1.5 py-0.5">{prog} in progress</span>
                  <span className="border border-green-300 bg-green-100 text-green-800 px-1.5 py-0.5">{covered} covered</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="rsaw-coverage-table">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-rose-200">
                      <th className="py-2 px-3 w-24">Requirement</th>
                      <th className="py-2 px-3">Auditor asks</th>
                      <th className="py-2 px-3">Evidence expected</th>
                      <th className="py-2 px-3 w-32">Coverage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cov.map((c, i) => {
                      const m = meta[c.status] || meta.gap;
                      return (
                        <tr key={i} className="border-b border-rose-100 align-top" data-testid={`rsaw-coverage-row-${i}`}>
                          <td className="py-2 px-3 font-semibold">{c.ref || "—"}{!c.in_star && <span className="block text-[10px] text-red-600 font-normal">not in STaR</span>}</td>
                          <td className="py-2 px-3 text-xs">{c.question || "—"}</td>
                          <td className="py-2 px-3 text-xs text-muted-foreground">{c.expected_evidence || "—"}</td>
                          <td className="py-2 px-3"><span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide border px-1.5 py-0.5 font-bold ${m.cls}`}>{c.status === "covered" ? <CheckCircle size={11} weight="fill" /> : c.status === "gap" ? <Warning size={11} weight="fill" /> : null}{m.label}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-rose-100">Coverage = "Covered" when a requirement has controls, documents or evidence linked in the STaR; "In progress" when only SMEs or action items are set; "Gap" when nothing is in place yet.</p>
            </div>
          );
        })()}

        {/* Implementation plan summary */}
        {t.impl_plan_summary && (
          <div className="border border-blue-200 bg-blue-50/50 p-4" data-testid="impl-summary">
            <div className="text-sm font-semibold text-[#071B3E] mb-1">Implementation Plan — key dates</div>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{t.impl_plan_summary}</p>
          </div>
        )}

        {/* AI-suggested defined-term mappings for the future standard */}
        {(t.suggested_terms || []).length > 0 && (
          <div className="border border-indigo-200 bg-indigo-50/40" data-testid="suggested-terms-panel">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-indigo-200 bg-indigo-100/40">
              <BookOpen size={16} weight="fill" className="text-indigo-700" />
              <span className="font-semibold text-[#071B3E]">Suggested defined terms for {t.future_standard_id}</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">— terms referenced in the new requirement language</span>
              {isSuperadmin && (
                <Button data-testid="apply-all-terms-btn" onClick={() => applyTerms(null)} disabled={busy === "terms"} size="sm" className="ml-auto rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
                  <Plus size={14} className="mr-1" />{busy === "terms" ? "Linking…" : "Link all to glossary"}
                </Button>
              )}
            </div>
            <div className="divide-y divide-indigo-100">
              {t.suggested_terms.map((s) => (
                <div key={s.definition_id} data-testid={`suggested-term-${s.definition_id}`} className="px-4 py-2 flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-[#071B3E]">{s.term}</span>
                  {s.is_new_introduction && <span className="text-[10px] uppercase tracking-wide border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5 font-bold">New Term</span>}
                  {s.is_revised_definition && <span className="text-[10px] uppercase tracking-wide border border-orange-400 bg-orange-50 text-orange-700 px-1.5 py-0.5 font-bold">Revised</span>}
                  {s.effective_date && <span className="text-[10px] font-mono text-muted-foreground">eff {s.effective_date}</span>}
                  {isSuperadmin && (
                    <button data-testid={`link-term-${s.definition_id}`} onClick={() => applyTerms([s.definition_id])} disabled={busy === "terms"}
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-300 px-1.5 py-0.5 hover:bg-indigo-100">
                      <Plus size={11} />Link
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-4" data-testid="transition-rows">
          {lowConfOnly && !t.rows.some((r) => r.grounding_check && r.grounding_check.confidence < lowConfThreshold) && (
            <p className="text-sm text-muted-foreground" data-testid="star-lowconf-empty">No verified rows below {lowConfThreshold}% — run "Verify grounding" to score more rows.</p>
          )}
          {(lowConfOnly ? t.rows.filter((r) => r.grounding_check && r.grounding_check.confidence < lowConfThreshold) : t.rows).map((r) => {
            const kind = r.kind || "requirement";
            const km = KIND_META[kind] || KIND_META.requirement;
            return (
            <div key={r.id} className={`border border-border ${km.indent}`} data-testid={`trow-${r.id}`} data-kind={kind}>
              <div className="flex items-center justify-between gap-3 px-4 py-2 bg-muted/50 border-b border-border">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] uppercase tracking-wide border px-1.5 py-0.5 font-bold shrink-0 ${km.cls}`} data-testid={`kind-${r.id}`}>{km.label}</span>
                  <span className="font-mono font-bold text-sm truncate">
                    {r.prev_req ? `${t.current_standard_id} ${r.prev_req}` : "—"} <span className="text-muted-foreground">→</span> {r.new_req ? `${t.future_standard_id} ${r.new_req}` : "—"}
                  </span>
                  {r.date_differs && (
                    <span data-testid={`date-differs-${r.id}`} title={`This part becomes enforceable ${r.effective_date}, which differs from its parent requirement (${r.parent_effective_date}).`}
                      className="text-[10px] uppercase tracking-wide border border-amber-400 bg-amber-50 text-amber-800 px-1.5 py-0.5 font-bold shrink-0 inline-flex items-center gap-1">
                      <Warning size={11} weight="fill" />Date differs · {r.effective_date}
                    </span>
                  )}
                </div>
                <Select value={r.disposition || ""} onValueChange={(v) => setRow(r.id, { disposition: v })} disabled={!canEdit}>
                  <SelectTrigger className="rounded-none h-7 w-40 text-xs shrink-0" data-testid={`dispo-${r.id}`}>
                    <SelectValue placeholder="Disposition" />
                  </SelectTrigger>
                  <SelectContent>{DISPOS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid md:grid-cols-2 gap-px bg-border">
                <div className="bg-card p-3"><div className="overline text-muted-foreground mb-1">Current language</div><p className="text-sm whitespace-pre-line">{r.prev_text || "—"}</p></div>
                <div className="bg-card p-3"><div className="overline text-muted-foreground mb-1">Future language</div><p className="text-sm whitespace-pre-line">{r.new_text || "—"}</p></div>
              </div>
              {r.difference && (
                <div className="px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkle size={14} weight="fill" className="text-blue-600" />
                    <span className="text-xs font-semibold">Difference (AI — decision support, verify)</span>
                    {r.disposition && <span className={`text-[10px] border px-1.5 py-0.5 ${DISPO_CLS[r.disposition] || ""}`}>{r.disposition}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">{r.difference}</p>
                </div>
              )}
              {r.good_looks_like && (
                <div className="px-4 py-3 border-t border-border bg-emerald-50/40" data-testid={`glf-${r.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Sparkle size={14} weight="fill" className="text-emerald-700" />
                    <span className="text-xs font-semibold">What good looks like (AI — SME readiness draft)</span>
                    {r.grounding_check && (
                      <span className={`text-[10px] border px-1.5 py-0.5 ${VERDICT_META[r.grounding_check.verdict]?.cls || ""}`} data-testid={`grounding-badge-${r.id}`}>
                        {r.grounding_check.confidence}% · {VERDICT_META[r.grounding_check.verdict]?.label || "Checked"}
                      </span>
                    )}
                    {editable && (
                      <button onClick={() => verifyRow(r.id)} disabled={busy === `verify-${r.id}`}
                        className="ml-auto text-[11px] inline-flex items-center gap-1 border border-border px-2 py-0.5 hover:bg-card disabled:opacity-50"
                        data-testid={`verify-grounding-btn-${r.id}`}
                        title="Reverse-check this narrative against the source material (1 AI credit)">
                        {busy === `verify-${r.id}` ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
                        {r.grounding_check ? "Re-check grounding" : "Verify grounding"}
                      </button>
                    )}
                    {editable && r.grounding_check && (
                      <button onClick={() => improveRow(r.id)} disabled={busy === `improve-${r.id}`}
                        className="text-[11px] inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 text-emerald-800 px-2 py-0.5 hover:bg-emerald-100 disabled:opacity-50"
                        data-testid={`improve-narrative-btn-${r.id}`}
                        title="Redraft this narrative using the grounding findings — removes/corrects unsupported claims (1 AI credit)">
                        {busy === `improve-${r.id}` ? <CircleNotch size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />}
                        Improve with grounding
                      </button>
                    )}
                  </div>
                  <div className="text-sm whitespace-pre-line leading-relaxed max-w-4xl">{r.good_looks_like}</div>
                  {r.grounding_check && (
                    <div className="mt-2 max-w-4xl">
                      <GroundingReport check={r.grounding_check} history={r.grounding_history} testid={`grounding-report-${r.id}`} />
                    </div>
                  )}
                </div>
              )}
              {/* What's in place today (auto-linked) */}
              {(r.linked_controls?.length || r.linked_documents?.length || r.linked_evidence?.length) ? (
                <div className="px-4 py-2 border-t border-border bg-muted/20 flex flex-wrap gap-1.5 items-center" data-testid={`links-${r.id}`}>
                  <span className="text-xs text-muted-foreground mr-1">In place today:</span>
                  {r.linked_controls?.map((c) => <span key={c.id} className="inline-flex items-center gap-1 text-xs bg-card border border-border px-2 py-0.5"><ShieldCheck size={11} />{c.name}</span>)}
                  {r.linked_documents?.map((d) => <span key={d.id} className="inline-flex items-center gap-1 text-xs bg-card border border-border px-2 py-0.5"><FileText size={11} />{d.title}</span>)}
                  {r.linked_evidence?.map((e) => <span key={e.id} className="inline-flex items-center gap-1 text-xs bg-card border border-border px-2 py-0.5"><Folders size={11} />{e.name}</span>)}
                </div>
              ) : null}
              {/* SME prep fields */}
              <div className="grid md:grid-cols-2 gap-3 p-4 border-t border-border">
                <div><label className="overline text-muted-foreground">SMEs</label><Input value={r.smes || ""} onChange={(e) => setRow(r.id, { smes: e.target.value })} className="rounded-none mt-1" disabled={!canEdit} /></div>
                <div><label className="overline text-muted-foreground">Current Policies &amp; Procedures</label><Input value={r.current_policies || ""} onChange={(e) => setRow(r.id, { current_policies: e.target.value })} className="rounded-none mt-1" disabled={!canEdit} /></div>
                <div><label className="overline text-muted-foreground">Controls</label><Input value={r.controls_text || ""} onChange={(e) => setRow(r.id, { controls_text: e.target.value })} className="rounded-none mt-1" disabled={!canEdit} /></div>
                <div><label className="overline text-muted-foreground">Action Items</label><Textarea value={r.action_items || ""} onChange={(e) => setRow(r.id, { action_items: e.target.value })} className="rounded-none mt-1 min-h-[40px]" disabled={!canEdit} /></div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
